"use client";

import { useState } from "react";
import { SpecOptionPills } from "@/components/listing/FurnitureOptionalDimensionsFields";
import {
  FAN_MOUNT_TYPES,
  FAN_POWER_SOURCES,
  type FanMountType,
  type FanPowerSource,
  type FanSpecFormState,
} from "@/lib/listing/fanListingSpec";
import { cn } from "@/lib/utils";

interface FanSpecFieldsProps {
  value: FanSpecFormState;
  onChange: (next: FanSpecFormState) => void;
  className?: string;
}

function formatMountLabel(value: FanMountType): string {
  switch (value) {
    case "standing":
      return "Standing";
    case "ceiling":
      return "Ceiling";
    case "wall":
      return "Wall";
  }
}

function formatPowerLabel(value: FanPowerSource): string {
  return value === "rechargeable" ? "Rechargeable" : "Non-rechargeable";
}

/**
 * Step 2 panel for canonical `fan` (Wave 2 fan unified Phase 2).
 * Mount + power required; blade size optional (collapsed).
 */
export default function FanSpecFields({
  value,
  onChange,
  className = "",
}: FanSpecFieldsProps) {
  const hasBlade =
    typeof value.bladeSizeInches === "number" &&
    Number.isFinite(value.bladeSizeInches);
  const [detailsOpen, setDetailsOpen] = useState(hasBlade);

  return (
    <div className={cn("space-y-6", className)}>
      <div className="space-y-2">
        <p className="text-sm font-medium text-foreground">
          How is it mounted? <span className="text-red-500">*</span>
        </p>
        <SpecOptionPills
          options={[...FAN_MOUNT_TYPES]}
          selected={value.fanMountType}
          onSelect={(fanMountType: FanMountType) =>
            onChange({ ...value, fanMountType })
          }
          formatLabel={formatMountLabel}
        />
      </div>

      <div className="space-y-2">
        <p className="text-sm font-medium text-foreground">
          Power source <span className="text-red-500">*</span>
        </p>
        <SpecOptionPills
          options={[...FAN_POWER_SOURCES]}
          selected={value.fanPowerSource}
          onSelect={(fanPowerSource: FanPowerSource) =>
            onChange({ ...value, fanPowerSource })
          }
          formatLabel={formatPowerLabel}
        />
      </div>

      <div className="space-y-2">
        <button
          type="button"
          onClick={() => setDetailsOpen((prev) => !prev)}
          className="flex w-full items-center justify-between rounded-lg border border-border bg-surface px-3 py-2 text-left text-sm font-medium text-foreground hover:border-border-strong"
          aria-expanded={detailsOpen}
        >
          <span>Add details — optional</span>
          <span className="text-xs font-normal text-text-light">
            {detailsOpen ? "Hide" : "Add"}
          </span>
        </button>
        {detailsOpen ? (
          <div className="space-y-2 rounded-lg border border-border bg-secondary/40 p-3">
            <label className="block text-sm font-medium text-foreground">
              Blade size (inches)
            </label>
            <input
              type="number"
              inputMode="decimal"
              min={6}
              max={60}
              step={1}
              value={value.bladeSizeInches === "" ? "" : value.bladeSizeInches}
              onChange={(e) => {
                const raw = e.target.value.trim();
                if (!raw) {
                  onChange({ ...value, bladeSizeInches: "" });
                  return;
                }
                const n = Number.parseFloat(raw);
                onChange({
                  ...value,
                  bladeSizeInches: Number.isFinite(n) ? n : "",
                });
              }}
              placeholder="e.g. 18"
              className="w-full rounded-lg border border-border bg-surface px-3 py-2 text-sm text-foreground"
            />
            <p className="text-xs text-text-light">
              Optional — skip if you don&apos;t know the size.
            </p>
          </div>
        ) : null}
      </div>
    </div>
  );
}
